import {
  Box,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import React from "react";

const styles = {
  paper: { p: 2 },
  jsonBox: {
    m: 0,
    mt: 1,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: 12,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
} satisfies Record<string, SxProps<Theme>>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** First present numeric value among the candidate keys, else null. */
const pickNumber = (
  usage: Record<string, unknown>,
  keys: readonly string[],
): number | null => {
  for (const key of keys) {
    const value = usage[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

/** First present string value among the candidate keys, else null. */
const pickString = (
  usage: Record<string, unknown>,
  keys: readonly string[],
): string | null => {
  for (const key of keys) {
    const value = usage[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return null;
};

/**
 * Renders the run's token-usage / cost summary from the opaque `usage` JSON bag
 * DE returns. The bag's documented keys are `model`, `inputTokens`, and
 * `outputTokens`; every field is probed defensively (alternate spellings
 * tolerated) and the full bag is shown beneath as a fallback so an unexpected
 * shape is never hidden.
 *
 * @param props The component props.
 * @param props.usage The `usage` JSON from `adhocPreviewResult` (may be null).
 */
const AdhocUsageLine = ({ usage }: { readonly usage: unknown }) => {
  if (isNil(usage)) {
    return (
      <Paper variant="outlined" sx={styles.paper}>
        <Typography gutterBottom variant="h6">
          Cost & usage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No usage recorded for this run.
        </Typography>
      </Paper>
    );
  }

  const bag = isRecord(usage) ? usage : {};
  const model = pickString(bag, ["model", "modelId"]);
  const inputTokens = pickNumber(bag, ["inputTokens", "promptTokens"]);
  const outputTokens = pickNumber(bag, ["outputTokens", "completionTokens"]);
  const totalTokens = pickNumber(bag, ["totalTokens"]);
  const costUsd = pickNumber(bag, ["costUsd", "cost", "usd"]);

  const parts: string[] = [];
  if (!isNil(model)) parts.push(`model: ${model}`);
  if (!isNil(inputTokens)) parts.push(`${inputTokens.toLocaleString()} in tok`);
  if (!isNil(outputTokens))
    parts.push(`${outputTokens.toLocaleString()} out tok`);
  if (!isNil(totalTokens))
    parts.push(`${totalTokens.toLocaleString()} total tok`);
  if (!isNil(costUsd)) parts.push(`$${costUsd.toFixed(4)}`);

  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Typography gutterBottom variant="h6">
        Cost & usage
      </Typography>
      {parts.length > 0 ? (
        <Typography variant="body2">{parts.join(" · ")}</Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No recognized usage fields; raw payload below.
        </Typography>
      )}
      <Box component="pre" sx={styles.jsonBox}>
        {JSON.stringify(usage, null, 2)}
      </Box>
    </Paper>
  );
};

export default AdhocUsageLine;
